// Contacts — the first pure-data document kind. The author never touches
// HTML: the editor is a form over ContactsDocument, storage is the rich_data
// envelope, and display is the strongly templated renderContactsHtml below.
// The template is a pure string builder (every field escaped) so the React
// renderer and the string display path share ONE template and the roundtrip
// harness can execute the full render.

import { escapeHtml } from './richElements'
import { parseDataDocument, serializeDataDocument } from './dataEnvelope'

export interface ContactEntry {
  name: string
  phone: string
  email: string
  note: string
}

export interface ContactsDocument {
  schemaVersion: 1
  contacts: ContactEntry[]
}

const CONTACTS_MAX = 200
const FIELD_MAX = 500
const NOTE_MAX = 2000
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function createContactEntry(): ContactEntry {
  return { name: '', phone: '', email: '', note: '' }
}

function coerceField(value: unknown, max: number): string {
  return typeof value === 'string' ? value.slice(0, max) : ''
}

function coerceEntry(raw: unknown): ContactEntry {
  const entry = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return {
    name: coerceField(entry.name, FIELD_MAX),
    phone: coerceField(entry.phone, FIELD_MAX),
    email: coerceField(entry.email, FIELD_MAX),
    note: coerceField(entry.note, NOTE_MAX),
  }
}

export function parseContactsDocument(html: string): ContactsDocument | null {
  const envelope = parseDataDocument(html)
  if (!envelope || envelope.kind !== 'contacts' || envelope.schemaVersion !== 1) return null
  const data = envelope.data as { contacts?: unknown } | null
  if (!data || !Array.isArray(data.contacts)) return null
  return { schemaVersion: 1, contacts: data.contacts.slice(0, CONTACTS_MAX).map(coerceEntry) }
}

export function serializeContactsDocument(doc: ContactsDocument): string {
  const contacts = doc.contacts.slice(0, CONTACTS_MAX).map(coerceEntry)
  const digest = contacts
    .map(contact => {
      const line = [contact.name, contact.phone, contact.email].map(part => part.trim()).filter(Boolean).join(' · ')
      const note = contact.note.trim()
      return note ? (line ? `${line} — ${note.slice(0, 120)}` : note.slice(0, 120)) : line
    })
    .filter(Boolean)
  return serializeDataDocument(
    { schemaVersion: 1, kind: 'contacts', data: { contacts } },
    digest.length ? digest : ['Contacts'],
  )
}

// --- strongly templated view -----------------------------------------------
// Fixed template slots, all values escaped; surfaces re-theme through the
// --contacts-* CSS vars (the same package-owned token pattern the content
// frames use). tel:/mailto: links are built only from validated characters.

function telHref(phone: string): string {
  const digits = phone.replace(/[^0-9+#*]/g, '')
  return digits.length >= 3 ? `tel:${digits}` : ''
}

const CARD_STYLE = 'background:var(--contacts-card-bg, #ffffff);border:var(--contacts-card-border, 1px solid rgba(15, 23, 32, 0.12));border-radius:var(--contacts-card-radius, 6px);padding:var(--contacts-card-padding, 10px 14px)'
const NAME_STYLE = 'font-weight:600;font-size:var(--contacts-name-size, 15px);color:var(--contacts-name-color, #17212b)'
const FIELD_STYLE = 'font-size:var(--contacts-field-size, 13px);color:var(--contacts-field-color, #314253)'
const LINK_STYLE = 'font-size:var(--contacts-field-size, 13px);color:var(--contacts-link-color, #2277aa)'
const NOTE_STYLE = 'margin-top:4px;font-size:var(--contacts-note-size, 12px);color:var(--contacts-note-color, #687684);white-space:pre-wrap'

export function renderContactsHtml(doc: ContactsDocument): string {
  const cards = doc.contacts.map(contact => {
    const parts: string[] = []
    parts.push(`<div style="${NAME_STYLE}">${escapeHtml(contact.name.trim() || 'Unnamed contact')}</div>`)
    const phone = contact.phone.trim()
    if (phone) {
      const href = telHref(phone)
      parts.push(href
        ? `<a href="${href}" style="${LINK_STYLE}">${escapeHtml(phone)}</a>`
        : `<span style="${FIELD_STYLE}">${escapeHtml(phone)}</span>`)
    }
    const email = contact.email.trim()
    if (email) {
      parts.push(EMAIL_RE.test(email)
        ? `<a href="mailto:${escapeHtml(email)}" style="${LINK_STYLE}">${escapeHtml(email)}</a>`
        : `<span style="${FIELD_STYLE}">${escapeHtml(email)}</span>`)
    }
    const note = contact.note.trim()
    if (note) parts.push(`<div style="${NOTE_STYLE}">${escapeHtml(note)}</div>`)
    return `<div class="rich_contact_card" style="${CARD_STYLE}"><div style="display:flex;flex-direction:column;gap:2px">${parts.join('')}</div></div>`
  })
  return `<div class="rich_contacts" style="display:flex;flex-direction:column;gap:var(--contacts-gap, 10px)">${cards.join('')}</div>`
}
